import React, {useState, useEffect} from 'react';
import Title from '../../../components/AccountSettings/Title';
import EditableTable from '../../admin/EditableTable';
import {ColumnSettings} from '../../admin/EditableTable/types';
import {useDispatch, useSelector} from 'react-redux';
import CheckedValue from '../../admin/CheckedValue';
import classnames from 'classnames';
import styles from './styles.module.scss';
import Icon from 'shared/components/icon';
import req from '../../../../shared/req';
import {flashMessage, errorMessage} from '../../../../shared/actions/notifications';
import LoadingIcon from 'shared/components/loading';
import Yup from '../../../utils/yup';
import AllowedDomainService from '../../../services/AllowedDomainService';
import {withFeatureFlags} from 'src/hooks/useFeatureFlags';
import DnsSettingsModal from 'src/apps/AdminUniversity/dns-settings-modal/dns-settings-modal';
import StatefulCheckbox from 'shared/components/stateful-checkbox';
import {
  DOMAIN_SEND_TOOLTIP,
  EMAIL_ALIAS_TOOLTIP,
} from 'src/apps/AdminUniversity/form/manage-allowed-email-domains/manage-allowed-email-domains';

interface Props {
  featureFlags: {
    canViewManageEmailMessaging: boolean;
    manageEmailAliases: boolean;
  };
}

interface DomainItem {
  id: string;
  domain?: string;
  fromEmailAlias?: boolean;
  domainVerifiedAt?: string;
  verifiedDomainSend?: boolean;
  sesVerificationToken?: string;
  sesDkimTokens?: string[];
}

interface EmailAliasItem {
  id: string;
  emailAddress: string;
  emailVerifiedAt?: string;
  fromEmailAlias?: boolean;
  verifiedDomainSend?: boolean;
}

const ManageEmailMessaging = ({featureFlags}: Props) => {
  const collegeId = useSelector((state: $TSFixMe) => state.user.universityId);
  const dispatch = useDispatch();
  const [loadingRow, setLoadingRow] = useState<string | null>(null);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [domainPermissions, setDomainPermissions] = useState<Map<string, DomainItem>>(new Map());
  const [dnsConfig, setDnsConfig] = useState<DomainItem | null>(null);
  const [checkboxOverrides, setCheckboxOverrides] = useState<Map<string, boolean>>(new Map());

  const getCheckboxValue = (
    item: EmailAliasItem,
    field: 'fromEmailAlias' | 'verifiedDomainSend'
  ) => {
    const key = `${item.id}-${field}`;
    return checkboxOverrides.has(key) ? checkboxOverrides.get(key)! : !!item[field];
  };

  const createCheckboxToggle =
    (item: EmailAliasItem, field: 'fromEmailAlias' | 'verifiedDomainSend') =>
    async (newValue: boolean) => {
      const oppositeField = field === 'fromEmailAlias' ? 'verifiedDomainSend' : 'fromEmailAlias';
      const data: Record<string, boolean> = {[field]: newValue};

      if (newValue && getCheckboxValue(item, oppositeField)) {
        data[oppositeField] = false;
        const fieldLabel = field === 'fromEmailAlias' ? 'Alias' : 'DNS';
        const oppositeLabel = field === 'fromEmailAlias' ? 'DNS' : 'Alias';
        dispatch(
          flashMessage(
            `${fieldLabel} is now enabled for this email, and ${oppositeLabel} has been switched off.`,
            'success',
            {},
            6000
          )
        );
      }

      try {
        await req({
          url: `/v1/university-email-aliases/${item.id}`,
          method: 'put',
          data,
        });
        setCheckboxOverrides((prev) => {
          const next = new Map(prev);
          next.set(`${item.id}-${field}`, newValue);
          if (data[oppositeField] === false) {
            next.set(`${item.id}-${oppositeField}`, false);
          }
          return next;
        });
      } catch {
        dispatch(errorMessage('Failed to update email alias'));
        throw new Error('Update failed');
      }
    };

  const getCheckboxPermissions = (
    item: EmailAliasItem,
    field: 'fromEmailAlias' | 'verifiedDomainSend'
  ) => {
    const emailDomain = item.emailAddress?.split('@')[1]?.toLowerCase();
    const domainPerms = emailDomain ? domainPermissions.get(emailDomain) : null;

    if (!item.emailVerifiedAt) {
      return {
        disabled: true,
        disabledTooltip: 'You must verify this email address before any permissions can be enabled',
      };
    }

    if (field === 'verifiedDomainSend') {
      if (!domainPerms?.verifiedDomainSend) {
        return {
          disabled: true,
          disabledTooltip: 'This permission is not available for this domain',
        };
      }
      if (!domainPerms?.domainVerifiedAt) {
        return {
          disabled: true,
          disabledTooltip: 'Domain must be verified before enabling this permission',
        };
      }
    }

    if (field === 'fromEmailAlias' && !domainPerms?.fromEmailAlias) {
      return {
        disabled: true,
        disabledTooltip: 'This permission is not available for this domain',
      };
    }

    return {disabled: false};
  };

  const domainColumns: ColumnSettings[] = [
    {
      name: 'domain',
      title: 'Email Domain',
      editable: false,
    },
    {
      name: 'domainVerifiedAt',
      title: 'DNS Verified?',
      editable: false,
      renderValue: (value, item) => {
        if (!item.verifiedDomainSend) {
          return <span>N/A</span>;
        }
        return CheckedValue(value);
      },
    },
    {
      name: 'verifiedDomainSend',
      title: 'DNS',
      editable: false,
      tooltip: DOMAIN_SEND_TOOLTIP,
      renderValue: CheckedValue,
    },
    {
      name: 'fromEmailAlias',
      title: 'Alias',
      editable: false,
      tooltip: EMAIL_ALIAS_TOOLTIP,
      renderValue: CheckedValue,
    },
    {
      name: 'dnsDetails',
      title: 'DNS Details',
      editable: false,
      renderValue: (_, item) => {
        if (!item.verifiedDomainSend) {
          return <span>N/A</span>;
        }
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDnsConfig({
                id: String(item.id),
                domain: String(item.domain),
                sesVerificationToken: String(item.sesVerificationToken),
                sesDkimTokens: item.sesDkimTokens as unknown as string[],
              });
            }}
          >
            View DNS Details
          </a>
        );
      },
    },
  ];

  const sendVerifyUniEmailAliasMailer = async (id) => {
    setLoadingRow(id);

    try {
      await req({
        url: `/v1/university-email-aliases/${id}/resend-email-verification`,
        method: 'post',
      });
      dispatch(flashMessage('A verification email has been sent.', 'success'));
    } catch (e) {
      dispatch(flashMessage(`Error: ${e}.`, 'error'));
    }

    setLoadingRow(null);
  };

  useEffect(() => {
    AllowedDomainService.index().then((response) => {
      setAllowedDomains(response.map(({domain}) => domain));

      const permissionsMap = new Map<string, DomainItem>();
      response.forEach((domainItem) => {
        if (domainItem.domain) {
          permissionsMap.set(domainItem.domain.toLowerCase(), {
            id: domainItem._id,
            domain: domainItem.domain,
            fromEmailAlias: domainItem.fromEmailAlias,
            verifiedDomainSend: domainItem.verifiedDomainSend,
            domainVerifiedAt: domainItem.domainVerifiedAt,
            sesVerificationToken: domainItem.sesVerificationToken,
            sesDkimTokens: domainItem.sesDkimTokens,
          });
        }
      });
      setDomainPermissions(permissionsMap);
    });
  }, []);

  const resendEmailButton = (value, itemValue) => {
    const emailItem = itemValue as unknown as EmailAliasItem;
    const emailDomain = emailItem.emailAddress?.split('@')[1]?.toLowerCase();
    const domainPerms = emailDomain ? domainPermissions.get(emailDomain) : null;

    if (!domainPerms?.fromEmailAlias) {
      return null;
    }

    if (value) {
      return null;
    }

    return itemValue.id === loadingRow ? (
      <LoadingIcon className={styles.rightIcon} />
    ) : (
      <button
        type="button"
        disabled={false}
        onClick={() => sendVerifyUniEmailAliasMailer(itemValue.id)}
        className={classnames(styles.deleteButton, styles.rightIcon)}
      >
        <Icon iconType="envelope" />
      </button>
    );
  };

  const basicColumns: ColumnSettings[] = [
    {
      name: 'emailAddress',
      title: 'Email',
      editable: true,
      width: '25%',
    },
    {
      name: 'emailVerifiedAt',
      title: '',
      editable: false,
      renderValue: (value, itemValue) => resendEmailButton(value, itemValue),
      width: '5%',
    },
    {
      name: 'emailVerifiedAt',
      title: 'Email Verified?',
      editable: false,
      renderValue: CheckedValue,
      width: '5%',
    },
  ];

  const columns: ColumnSettings[] = [
    {
      name: 'emailAddress',
      title: 'Email',
      editable: true,
      width: '25%',
    },
    {
      name: 'emailVerifiedAt',
      title: '',
      editable: false,
      renderValue: (value, itemValue) => resendEmailButton(value, itemValue),
      width: '5%',
    },
    {
      name: 'emailVerifiedAt',
      title: 'Email Verified?',
      editable: false,
      renderValue: CheckedValue,
      width: '5%',
    },
    {
      name: 'fromEmailAlias',
      title: 'Alias',
      editable: false,
      tooltip: EMAIL_ALIAS_TOOLTIP,
      renderValue: (_value, item) => {
        const emailItem = item as unknown as EmailAliasItem;
        const permissions = getCheckboxPermissions(emailItem, 'fromEmailAlias');
        const checked = getCheckboxValue(emailItem, 'fromEmailAlias');
        return (
          <StatefulCheckbox
            key={`${emailItem.id}-alias-${checked}`}
            initialChecked={checked}
            onToggle={createCheckboxToggle(emailItem, 'fromEmailAlias')}
            disabled={permissions.disabled}
            disabledTooltip={permissions.disabledTooltip}
            wrapper={(checkbox) => <div className={styles.centeredCheckbox}>{checkbox}</div>}
          />
        );
      },
    },
    {
      name: 'verifiedDomainSend',
      title: 'DNS',
      editable: false,
      tooltip: DOMAIN_SEND_TOOLTIP,
      renderValue: (_value, item) => {
        const emailItem = item as unknown as EmailAliasItem;
        const permissions = getCheckboxPermissions(emailItem, 'verifiedDomainSend');
        const checked = getCheckboxValue(emailItem, 'verifiedDomainSend');
        return (
          <StatefulCheckbox
            key={`${emailItem.id}-domain-${checked}`}
            initialChecked={checked}
            onToggle={createCheckboxToggle(emailItem, 'verifiedDomainSend')}
            disabled={permissions.disabled}
            disabledTooltip={permissions.disabledTooltip}
            wrapper={(checkbox) => <div className={styles.centeredCheckbox}>{checkbox}</div>}
          />
        );
      },
    },
  ];

  const FormSchema = Yup.object().shape({
    emailAddress: Yup.string()
      .email('Invalid email')
      .test(
        'is-valid-domain',
        'Please contact your customer success manager if you’d like to add a new email domain.',
        (value) => {
          if (!value) return false;
          return allowedDomains.some((domain) => value.includes(domain));
        }
      ),
  });

  return (
    <div
      className={classnames('account-settings-form', styles.emailMessaging)}
      data-testid="manage-email-aliases"
    >
      {featureFlags.canViewManageEmailMessaging && (
        <>
          <Title>Allowed Email Domains</Title>
          <span>
            To update your allowed domains, please reach out to your Customer Success Manager.
          </span>
          <EditableTable
            basePath={`/v1/admin/allowed-email-domains?university_id=${collegeId}`}
            defaultSortKey="domain"
            columns={domainColumns}
            identifier="allowed-email-domains-view"
            disableUpdating
            disableAdding
            disableDeleting
            inContainer
          />
          <DnsSettingsModal
            show={!!dnsConfig}
            onDismiss={() => setDnsConfig(null)}
            dnsConfig={dnsConfig}
          />
          <Title>Manage Emails</Title>
          <EditableTable
            basePath="/v1/university-email-aliases?fields=emailAddress,universityId,emailVerifiedAt,fromEmailAlias,verifiedDomainSend"
            defaultSortKey="emailAddress"
            columns={columns}
            identifier="university-email-aliases-advanced"
            defaultValues={{universityId: collegeId}}
            disableUpdating
            inContainer
            formSchema={FormSchema}
            afterCreate={() =>
              dispatch(
                flashMessage(
                  'A verification email has been sent. This email cannot be used for Alias or DNS until it is verified.',
                  'success',
                  {},
                  8000
                )
              )
            }
          />
        </>
      )}
      {featureFlags.manageEmailAliases && !featureFlags.canViewManageEmailMessaging && (
        <>
          <Title>Manage Emails</Title>
          <EditableTable
            basePath="/v1/university-email-aliases?fields=emailAddress,universityId,emailVerifiedAt"
            defaultSortKey="emailAddress"
            columns={basicColumns}
            identifier="university-email-aliases-basic"
            defaultValues={{universityId: collegeId}}
            disableUpdating
            inContainer
            formSchema={FormSchema}
          />
        </>
      )}
    </div>
  );
};

export default withFeatureFlags(
  ManageEmailMessaging,
  ['canViewManageEmailMessaging', 'manageEmailAliases'],
  true
);
